import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { Employee } from "../../components/course/types";
import { Check, Edit, Eye, FileDown, PlusCircle, User, UserPlus, X } from "lucide-react";
import { useToast } from "../../components/ui/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useNavigate } from "react-router-dom";

const AllEmployees = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [viewingEmployee, setViewingEmployee] = useState<Employee | null>(null);
  
  // Sample data - in a real app, this would come from an API
  const [employees, setEmployees] = useState<Employee[]>([
    {
      id: "1",
      name: "John Smith",
      email: "john.smith@example.com",
      phone: "+1 (555) 123-4567",
      designation: "Senior Developer",
      department: "Engineering",
      joiningDate: new Date("2022-05-15"),
      courses: ["React Advanced", "System Architecture"],
      status: "Active",
      employeeId: "EMP10001",
    },
    {
      id: "2",
      name: "Sarah Johnson",
      email: "sarah.j@example.com",
      phone: "+1 (555) 234-5678",
      designation: "UI/UX Designer",
      department: "Design",
      joiningDate: new Date("2022-06-20"),
      courses: ["UI/UX Fundamentals", "Design Systems"],
      status: "Active",
      employeeId: "EMP10002",
    },
    {
      id: "3",
      name: "Michael Wong",
      email: "michael.w@example.com",
      phone: "+1 (555) 345-6789",
      designation: "Project Manager",
      department: "Product",
      joiningDate: new Date("2021-11-10"),
      courses: ["Agile Project Management", "Leadership Skills"],
      status: "Suspended",
      employeeId: "EMP10003",
    },
    {
      id: "4",
      name: "Emily Davis",
      email: "emily.d@example.com",
      phone: "+1 (555) 456-7890",
      designation: "QA Engineer",
      department: "Quality Assurance",
      joiningDate: new Date("2022-02-15"),
      courses: ["Test Automation", "QA Methodologies"],
      status: "OffBoarded",
      employeeId: "EMP10004",
    },
    {
      id: "5",
      name: "Robert Chen",
      email: "robert.c@example.com",
      phone: "+1 (555) 567-8901",
      designation: "Backend Developer",
      department: "Engineering",
      joiningDate: new Date("2021-08-05"),
      courses: ["Node.js Advanced", "Database Optimization"],
      status: "Terminated",
      employeeId: "EMP10005",
    },
  ]);

  const handleRegisterEmployee = () => {
    navigate("/employees/register");
  };

  const handleViewDetails = (employee: Employee) => {
    setViewingEmployee(employee);
  };

  const handleEditEmployee = (id: string) => {
    navigate(`/employees/edit/${id}`);
  };

  const handleStatusChange = (id: string, newStatus: "Active" | "Suspended" | "OffBoarded" | "Terminated") => {
    setEmployees(employees.map(employee => {
      if (employee.id === id) {
        return { ...employee, status: newStatus };
      }
      return employee;
    }));

    const targetEmployee = employees.find(e => e.id === id);
    
    toast({
      title: "Status Updated",
      description: `${targetEmployee?.name}'s status changed to ${newStatus}.`,
    });
  };

  // Apply filters
  let filteredEmployees = employees;
  
  if (searchTerm) {
    filteredEmployees = filteredEmployees.filter(
      (employee) =>
        employee.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.employeeId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
        employee.designation.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
  
  if (statusFilter) {
    filteredEmployees = filteredEmployees.filter(
      (employee) => employee.status === statusFilter
    );
  }

  const renderStatusBadge = (status: string) => {
    switch (status) {
      case "Active":
        return <Badge variant="default" className="bg-green-500">Active</Badge>;
      case "Suspended":
        return <Badge variant="outline" className="bg-yellow-100">Suspended</Badge>;
      case "OffBoarded":
        return <Badge variant="secondary">Off-Boarded</Badge>;
      case "Terminated":
        return <Badge variant="destructive">Terminated</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="container py-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text -2xl font-bold">All Employees</h1>
        <div className="flex space-x-2">
          <Button variant="outline"><FileDown className="mr-2" /> Export List</Button>
          <Button onClick={handleRegisterEmployee}><UserPlus className="mr-2" /> Register New Employee</Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Employees Directory</CardTitle>
          <CardDescription>
            Manage all employees and their details
          </CardDescription>
          <div className="mt-4 flex flex-col sm:flex-row gap-4">
            <Input
              placeholder="Search by name, ID, department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-md"
            />
            <Select value={statusFilter || "All"} onValueChange={(value) => setStatusFilter(value || null)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All Statuses</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Suspended">Suspended</SelectItem>
                <SelectItem value="OffBoarded">Off-Boarded</SelectItem>
                <SelectItem value="Terminated">Terminated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Name</TableHead>
                {/* <TableHead>Department</TableHead> */}
                <TableHead>Designation</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Joining Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEmployees.map((employee) => (
                <TableRow key={employee.id}>
                  <TableCell className="font-medium">{employee.employeeId}</TableCell>
                  <TableCell>
                    <div>
                      <div>{employee.name}</div>
                      <div className="text-sm text-gray-500">{employee.email}</div>
                    </div>
                  </TableCell>
                  {/* <TableCell>{employee.department}</TableCell> */}
                  <TableCell>{employee.designation}</TableCell>
                  <TableCell>{renderStatusBadge(employee.status)}</TableCell>
                  <TableCell>{employee.joiningDate.toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="outline" size="sm" onClick={() => handleViewDetails(employee)}>
                            <Eye size={16} className="mr-1" /> View
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          <DialogHeader>
                            <DialogTitle>Employee Details</DialogTitle>
                            <DialogDescription>
                              Detailed information about {viewingEmployee?.name}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">ID:</span>
                              <span className="col-span-3">{viewingEmployee?.employeeId}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Name:</span>
                              <span className="col-span-3">{viewingEmployee?.name}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Email:</span>
                              <span className="col-span-3">{viewingEmployee?.email}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Phone:</span>
                              <span className="col-span-3">{viewingEmployee?.phone}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Department:</span>
                              <span className="col-span-3">{viewingEmployee?.department}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Designation:</span>
                              <span className="col-span-3">{viewingEmployee?.designation}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Joining Date:</span>
                              <span className="col-span-3">{viewingEmployee?.joiningDate.toLocaleDateString()}</span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Status:</span>
                              <span className="col-span-3">
                                {viewingEmployee && renderStatusBadge(viewingEmployee.status)}
                              </span>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                              <span className="font-semibold text-right">Courses:</span>
                              <span className="col-span-3">
                                {viewingEmployee?.courses.join(", ")}
                              </span>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                      <Button variant="outline" size="sm" onClick={() => handleEditEmployee(employee.id)}>
                        <Edit size={16} className="mr-1" /> Edit
                      </Button>
                      
                      {employee.status === "Active" ? (
                        <Select
                          onValueChange={(value) => handleStatusChange(employee.id, value as any)}
                          defaultValue={employee.status}
                        >
                          <SelectTrigger className="h-9 w-[120px]">
                            <SelectValue placeholder="Status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Active">Active</SelectItem>
                            <SelectItem value="Suspended">Suspend</SelectItem>
                            <SelectItem value="OffBoarded">Off-Board</SelectItem>
                            <SelectItem value="Terminated">Terminate</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Button 
                          variant="default" 
                          size="sm" 
                          className="bg-green-600 hover:bg-green-700" 
                          onClick={() => handleStatusChange(employee.id, "Active")}
                        >
                          <Check size={16} className="mr-1" /> Activate
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AllEmployees;
